import type { RowData } from "@tanstack/react-table";
import { CellText } from "./CellText";
import { toDisplayText } from "./cellValues";
import { EmptyCellValue } from "./EmptyCellValue";
import { HighlightedText } from "./HighlightedText";
import type { DataGridCellProps } from "./types";
import styles from "./cellContent.module.scss";

/**
 * A value formatted per its column kind (`toDisplayText`) in tabular
 * figures. Never wraps or clips: a value wider than its column truncates
 * with the full value in a tooltip. Search highlights the shown text.
 * Missing or invalid values render the column's empty value.
 */
const FormattedCell = <TData extends RowData>({
    context,
    meta,
}: Readonly<DataGridCellProps<TData>>) => {
    const text = toDisplayText(context.getValue(), meta);
    if (text === null) {
        return <EmptyCellValue empty={meta.empty} />;
    }
    return (
        <CellText text={text} overflow="truncate" tone={meta.tone}>
            <span className={styles.numeric}>
                <HighlightedText text={text} table={context.table} />
            </span>
        </CellText>
    );
};

/** Default number cell: grouped digits ("1,250.5"). */
export const NumberCell = FormattedCell;

/** Default money cell: USD with a leading minus for negatives ("-$1,250.50"). */
export const MoneyCell = FormattedCell;

/**
 * Default cell for `dateOnly` (`YYYY-MM-DD`) values. Hides the year for the
 * current year unless the column sets `showYear`.
 */
export const DateCell = FormattedCell;
